import { useMemo, useState } from "react";
import { ArticleReview } from "#/services/articleReviews";
import { ActionButton } from "#/components/shared/buttons/ActionButtons";
import { ArticleReviewListItem } from "#/admin/article-reviews/components/ArticleReviewListItem";

export type ArticleReviewListProps = {
  items: ArticleReview[];
  showArticleId?: boolean;
  showRevisionId?: boolean;
  showActions?: boolean;
  onViewReview?: (reviewId: string) => void;
};

export function ArticleReviewList({
  items,
  showArticleId = true,
  showRevisionId = true,
  showActions = false,
  onViewReview,
}: ArticleReviewListProps) {
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});

  const allIds = useMemo(
    () => items.map((item) => item.id).filter((id): id is string => Boolean(id)),
    [items]
  );

  const allExpanded = allIds.length > 0 && allIds.every((id) => expandedIds[id]);

  return (
    <div className="space-y-2">
      <div className="flex justify-end gap-2">
        <ActionButton
          size="sm"
          tone="secondary"
          onClick={() => {
            const next: Record<string, boolean> = {};
            allIds.forEach((id) => {
              next[id] = true;
            });
            setExpandedIds(next);
          }}
          disabled={!allIds.length || allExpanded}
        >
          Expand all notes
        </ActionButton>
        <ActionButton
          size="sm"
          tone="secondary"
          onClick={() => setExpandedIds({})}
          disabled={!allIds.length || !Object.keys(expandedIds).length}
        >
          Collapse all notes
        </ActionButton>
      </div>
      <div className="overflow-x-auto">
      <table className="min-w-full text-left text-sm">
        <thead className="bg-cream-50 text-brown-700">
          <tr>
            <th className="px-4 py-3 font-semibold">Review ID</th>
            {showArticleId ? (
              <th className="px-4 py-3 font-semibold">Article ID</th>
            ) : null}
            {showRevisionId ? (
              <th className="px-4 py-3 font-semibold">Revision ID</th>
            ) : null}
            <th className="px-4 py-3 font-semibold">Status</th>
            <th className="px-4 py-3 font-semibold">Created</th>
            <th className="px-4 py-3 font-semibold">Review notes</th>
            {showActions ? (
              <th className="px-4 py-3 font-semibold">Details</th>
            ) : null}
          </tr>
        </thead>
        <tbody className="divide-y divide-beige-100">
          {items.map((item) => (
            <ArticleReviewListItem
              key={item.id}
              item={item}
              showArticleId={showArticleId}
              showRevisionId={showRevisionId}
              showActions={showActions}
              onViewReview={onViewReview}
              expanded={Boolean(expandedIds[item.id])}
              onToggleExpanded={() =>
                setExpandedIds((prev) => ({
                  ...prev,
                  [item.id]: !prev[item.id],
                }))
              }
            />
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

export default ArticleReviewList;
